import type { CaptureResult } from 'posthog-js';

/**
 * Shape of a single stack frame in the client-side `$exception_list`.
 */
interface ExceptionFrame {
  filename?: string;
  source?: string;
}

/**
 * Shape of an entry in the `$exception_list` property that the
 * PostHog browser SDK attaches to `$exception` events.
 */
interface ExceptionListEntry {
  type?: string;
  value?: string;
  stacktrace?: { frames?: ExceptionFrame[] };
}

// ---------------------------------------------------------------------------
// Strategy 1: Message-based filtering
// ---------------------------------------------------------------------------

/**
 * Exception messages that are known to be unactionable.
 *
 * - "Script error."          — generic cross-origin error with no useful info.
 * - "EmptyRanges"            — Safari/WebKit browser bug (Selection API).
 *
 * NOTE: "$ is not defined" is NOT included here because the docs page loads
 * jQuery + Webflow scripts (DocumentationLanding.tsx). If jQuery CDN fails,
 * a legitimate "$ is not defined" from our own page would be silently dropped.
 * GTM-originated "$ is not defined" errors are caught by the frame-based
 * strategy instead (gtm.js / googletagmanager.com patterns).
 */
export const UNACTIONABLE_MESSAGE_PATTERNS: readonly string[] = [
  'Script error.',
  'EmptyRanges',
];

// ---------------------------------------------------------------------------
// Strategy 2: Frame-based filtering (third-party domains)
// ---------------------------------------------------------------------------

/**
 * Domain-level patterns that identify third-party scripts whose exceptions
 * are not actionable. Matched against the `filename` (or `source`) of each
 * stack frame in `$exception_list`.
 *
 * - vector.co      — Vector CDN (serves pixel.js / reb2b tracking).
 * - cdn.reb2b.com  — reb2b visitor identification CDN.
 * - gtm.js         — Google Tag Manager inline script.
 * - googletagmanager.com — Google Tag Manager CDN.
 */
export const THIRD_PARTY_FRAME_PATTERNS: readonly string[] = [
  'vector.co',
  'cdn.reb2b.com',
  'gtm.js',
  'googletagmanager.com',
];

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` when any entry in the exception list has a `value` that
 * matches a known unactionable message pattern.
 */
function hasUnactionableMessage(exceptionList: ExceptionListEntry[]): boolean {
  return exceptionList.some((entry) => {
    const value = entry?.value;
    if (typeof value !== 'string') return false;
    return UNACTIONABLE_MESSAGE_PATTERNS.some((pattern) =>
      value.includes(pattern)
    );
  });
}

/**
 * Returns `true` when **every** exception entry in the list has **all** its
 * stack frames originating from a known third-party script.
 *
 * Entries with missing or empty frames are NOT considered third-party
 * (we can't determine origin, so we keep them). If any entry lacks frames,
 * the function returns `false` to be conservative.
 */
function isAllFramesThirdParty(exceptionList: ExceptionListEntry[]): boolean {
  // Every entry must have non-empty frames that are all third-party.
  // If any entry lacks frames, we can't determine its origin — keep the event.
  return (
    exceptionList.length > 0 &&
    exceptionList.every((entry) => {
      const frames = entry?.stacktrace?.frames;
      if (!frames || frames.length === 0) return false;

      return frames.every((frame) => {
        if (!frame || typeof frame !== 'object') return false;
        const filename = frame.filename || frame.source || '';
        return THIRD_PARTY_FRAME_PATTERNS.some((pattern) =>
          filename.includes(pattern)
        );
      });
    })
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns `true` when a `$exception` CaptureResult should be dropped because
 * it is unactionable — either a known browser/third-party message or all
 * stack frames originate from third-party scripts.
 */
export function isUnactionableException(event: CaptureResult | null): boolean {
  if (!event || event.event !== '$exception') return false;

  const exceptionList: unknown = event.properties?.$exception_list;
  if (!Array.isArray(exceptionList) || exceptionList.length === 0) return false;

  // Filter out non-object entries defensively
  const validEntries = exceptionList.filter(
    (entry): entry is ExceptionListEntry =>
      typeof entry === 'object' && entry !== null
  );
  if (validEntries.length === 0) return false;

  // Strategy 1: known unactionable messages
  if (hasUnactionableMessage(validEntries)) return true;

  // Strategy 2: all frames from third-party scripts
  if (isAllFramesThirdParty(validEntries)) return true;

  return false;
}

/**
 * PostHog `before_send` hook that drops unactionable exceptions.
 *
 * Returns `null` to drop the event, or the original event to keep it.
 * Handles the `null` input case required by the `BeforeSendFn` signature.
 */
export function filterUnactionableExceptions(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return null;
  return isUnactionableException(event) ? null : event;
}
